import React, { useState, useEffect, useRef } from 'react';
import { getCompanies } from '../../api/companies';
import { Company } from '../../types';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
};

interface Props {
  /** UUID der gewählten Firma (controlled) */
  value: string;
  /** Anzeigename der aktuell gewählten Firma (für Edit-Modus) */
  initialName?: string;
  onChange: (id: string, name: string) => void;
  placeholder?: string;
}

export function CompanyAutocomplete({ value, initialName, onChange, placeholder = 'Firmenname eingeben…' }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Company[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState(initialName ?? '');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Wenn von außen ein value gesetzt wird (z.B. im Edit-Modus) und initialName vorhanden
  useEffect(() => {
    if (value && initialName && !selectedName) setSelectedName(initialName);
  }, [value, initialName]);

  // Klick außerhalb schließt Dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = (q: string) => {
    setQuery(q);
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(() => {
      getCompanies({ limit: 15, search: q }).then(r => {
        setResults(r.data);
        setOpen(true);
      }).catch(() => {});
    }, 250);
  };

  const select = (c: Company) => {
    onChange(c.id, c.display_name);
    setSelectedName(c.display_name);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('', '');
    setSelectedName('');
    setQuery('');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selectedName ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
          background: 'var(--surface2)', fontSize: '13px',
        }}>
          <span style={{ fontWeight: 500 }}>🏢 {selectedName}</span>
          <button type="button" onClick={clear} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text3)', fontSize: '18px', lineHeight: 1, padding: '0 2px',
          }}>×</button>
        </div>
      ) : (
        <input
          style={inputStyle}
          value={query}
          onChange={e => search(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
      )}

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
          background: 'var(--surface)', border: '1px solid var(--border2)',
          borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: '240px', overflowY: 'auto', marginTop: '3px',
        }}>
          {results.length > 0 ? results.map(c => (
            <div key={c.id} onMouseDown={() => select(c)} style={{
              padding: '9px 12px', cursor: 'pointer', fontSize: '13px',
              borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div>
                <span style={{ fontWeight: 500 }}>{c.display_name}</span>
                {c.city && <span style={{ color: 'var(--text3)', marginLeft: '8px', fontSize: '11px' }}>{c.city}</span>}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {(c.profile_count ?? 0) > 0 && (
                  <span style={{ fontSize: '10px', padding: '1px 6px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: '10px' }}>
                    {c.profile_count} Profile
                  </span>
                )}
                {(c.contact_count ?? 0) > 0 && (
                  <span style={{ fontSize: '10px', padding: '1px 6px', background: 'var(--surface2)', color: 'var(--text3)', borderRadius: '10px' }}>
                    {c.contact_count} Kontakte
                  </span>
                )}
              </div>
            </div>
          )) : (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text3)' }}>
              Keine Firma gefunden
            </div>
          )}
        </div>
      )}
    </div>
  );
}
